import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  CloseResisterInterface,
  NosERPSearchClosings,
  OpenRegisterInterface,
  Register as NosRegister,
  RegisterStatus as NosRegisterStatus,
  SearchClosingsResult as NosSearchClosingsResult
} from '../models/register.model';
import {
  CloseRegister,
  OpenRegister,
  Register,
  RegisterStatus,
  SearchClosingsParams,
  SearchClosingsResult
} from '../../../models/register.model';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RegisterServiceAdapter {
  private registers: BehaviorSubject<Register[]> = new BehaviorSubject<Register[]>([]);
  private closingStatus: BehaviorSubject<{ status: RegisterStatus }> = new BehaviorSubject<{
    status: RegisterStatus;
  }>({ status: RegisterStatus.NONE_OPEN }); // Default value

  constructor(private _httpClient: HttpClient) {
    this._httpClient.get<Register[]>('api/registers').subscribe(result => {
      this.registers.next(result); // Update the BehaviorSubject with fetched registers
    });
    const user = JSON.parse(localStorage.getItem('user')).id_utilizador;
    this.getClosingStatus(user).subscribe(result => {});
  }

  // Mappings for input/output transformations

  private mapToOpenRegister(input: OpenRegisterInterface): OpenRegister {
    return {
      cashBoxId: input.id_caixa,
      employeeId: input.id_funcionario,
      userId: input.id_utilizador,
      initialValue: input.value_initial
    };
  }

  private mapToCloseRegister(input: CloseResisterInterface): CloseRegister {
    return {
      closeId: input.id_fecho,
      userId: input.id_utilizador,
      closingUserId: input.id_utilizador_fecho,
      isManager: input.gerente === 1,
      closingBalance: input.last_value
    };
  }

  private mapToSearchClosingsParams(input: SearchClosingsParams): NosERPSearchClosings {
    return {
      box: input.cashBox,
      data: null,
      id_utilizador: input.userId,
      user: input.username
    };
  }

  private mapRegisterToNosRegister(input: NosRegister): Register {
    const user = JSON.parse(localStorage.getItem('user'));
    return {
      bankId: input.id_banco,
      name: input.nome,
      accountNumber: input.numero_conta,
      nib: input.nib,
      iban: input.iban,
      balance: input.saldo,
      employeeId: user.id_funcionario,
      accountingPlanId: input.id_planocontabilistico,
      bankTypeId: input.id_tipobanco,
      status: input.estado,
      visibleOnInvoice: input.visivel_fatura === '1',
      associatedBankId: input.banco_associado,
      printerDetails: {
        printerName: input.impresora,
        ipAddress: input.ip,
        port: input.porta
      }
    };
  }

  private mapSearchClosingsResult(input: NosSearchClosingsResult): SearchClosingsResult {
    return {
      data: {
        closingEntries: input.data.map(entry => ({
          entryId: entry.id,
          startDate: entry.data_inicio,
          endDate: entry.data_fim,
          user: entry.utilizador,
          bankId: entry.id_banco,
          bankDetails: {
            bankId: entry.banco.id_banco,
            name: entry.banco.nome,
            accountNumber: entry.banco.numero_conta,
            balance: entry.banco.saldo,
            nib: entry.banco.nib,
            iban: entry.banco.iban,
            employeeId: entry.banco.id_funcionario,
            accountingPlanId: entry.banco.id_planocontabilistico,
            status: entry.banco.estado,
            visibleOnInvoice: entry.banco.visivel_fatura === '1'
          },
          closingUser: entry.utilizador_fecho
        }))
      }
    };
  }

  // Methods using the mappings
  openRegister(params: OpenRegister): Observable<{ status: RegisterStatus }> {
    const convertedParams: OpenRegisterInterface = {
      id_caixa: params.cashBoxId,
      id_funcionario: params.employeeId,
      id_utilizador: params.userId,
      value_initial: params.initialValue
    };

    return this._httpClient.post<{ status: NosRegisterStatus }>('api/detailsCaixa', convertedParams).pipe(
      switchMap(() => {
        const user = JSON.parse(localStorage.getItem('user')).id_utilizador;
        return this.getClosingStatus(user);
      })
    );
  }

  searchClosings(params: SearchClosingsParams): Observable<SearchClosingsResult> {
    const convertedParams = this.mapToSearchClosingsParams(params);
    return this._httpClient
      .post<NosSearchClosingsResult>('api/search-fecho', convertedParams)
      .pipe(map(result => this.mapSearchClosingsResult(result)));
  }

  getAllRegisters(): Observable<Register[]> {
    return this._httpClient.get<NosRegister[]>('api/registers').pipe(map(items => items.map(item => this.mapRegisterToNosRegister(item))));
  }

  /**
   * Dynamically determines the current register
   */
  get currentRegister(): Register | null {
    const allRegisters = this.registers.getValue();
    return allRegisters.length > 0 ? allRegisters[0] : null;
  }

  /**
   * Returns a shared observable of the closing status
   */
  get closingStatus$(): Observable<{ status: RegisterStatus }> {
    return this.closingStatus.asObservable();
  }

  /**
   * Fetches the closing status based on the current Register and the user
   * @param idUser - ID of the user
   * @param registerId - Optional Register ID
   * @returns Observable<{ status: RegisterStatus }>
   */
  getClosingStatus(idUser: number, registerId?: number): Observable<{ status: RegisterStatus }> {
    return this.getAllRegisters().pipe(
      map(registers => {
        const activeRegister = registerId || (registers.length > 0 ? registers[0].bankId : null);
        return activeRegister;
      }),
      switchMap(registerId => {
        if (!registerId) {
          const result = { status: RegisterStatus.HAS_OPEN };
          this.closingStatus.next(result); // Emit the new status to the BehaviorSubject
          return of(result);
        }
        return this._httpClient
          .get<{
            status: RegisterStatus;
          }>(`api/searchDetailsBox/${idUser}/${registerId}`)
          .pipe(
            map(response => {
              this.closingStatus.next(response); // Update the closing status
              return response;
            })
          );
      })
    );
  }

  /**
   * Close the current register
   * @param params - Parameters to close the register
   */
  closeRegister(params: CloseRegister): Observable<{ status: number }> {
    const adaptedParams = {
      id_fecho: params.closeId,
      id_utilizador: params.userId,
      id_utilizador_fecho: params.closingUserId,
      gerente: params.isManager ? 1 : 0,
      last_value: params.closingBalance
    };
    return this._httpClient.post<{ status: number }>('api/finhishFecho', adaptedParams).pipe(
      switchMap(() => {
        // After closing, refresh the closing status
        // Assuming `params.id_utilizador_fecho` is the current user ID
        return this.getClosingStatus(params.closingUserId);
      })
    );
  }

  searchFechoDetails(payload: any): Observable<any> {
    return this._httpClient.post<any>('api/search-fecho-details', payload);
  }

  /**
   * Update register (caixa) printer configuration
   * @param payload { id_utilizador, id_banco, ip, porta }
   */
  updateRegisterPrinterConfig(payload: { id_utilizador: number; id_banco: number; ip: string; porta: string }): Observable<any> {
    return this._httpClient.post<any>('api/config/editCaixa', payload);
  }
}
